














































































































































import { PhotosService } from '~/services/photos/PhotosService'
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  toRefs
} from '@nuxtjs/composition-api'
import { useClassifiedForm } from '~/compositions/form'
import { formFieldProps } from '~/constants/form'
import { faInfo } from '@fortawesome/free-solid-svg-icons'
import { useNamespacedStore } from '~/compositions/store'
import {
  CLASSIFIED_FORM_NS,
  FormState
} from '~/store/modules/shared/classifieds/form/state'
import { useDeps } from '~/compositions/dependency-container'
import {
  DEFAULT_MAX_NATIVE_CLASSIFIED_PHOTOS,
  DEFAULT_MAX_PANORAMIC_CLASSIFIED_PHOTOS
} from '~/constants/classified/photos'
import { Photo } from '~/models/photos/types'
import { UploaderFile } from '~/models/photos/uploader'
import { useId } from '~/compositions/id'
import { useConcurrentQueue } from '~/compositions/queue'
import FieldError from '~/components/car/classifieds/form/fieldsets/fields/FieldError.vue'
import { ci360Logo } from '~/icons/source/solid/360logo'
import { useFile } from '~/compositions/files/file'
import CFileUploader from '~/components/shared/configurable/file/CFileUploader.vue'
import { useI18n } from '~/compositions/i18n'
import UserAgentService from '~/services/UserAgentService'
import CPagination from '~/components/shared/configurable/pagination/CPagination.vue'
import { useLogger } from '~/compositions/logger'
import { defineComponentTranslations } from '~/utils/i18n'
import CCorruptedFilesModal from '~/components/shared/configurable/modal/CCorruptedFilesModal.vue'
import { vue3Set } from '~/utils/nuxt3-migration'

export default defineComponent({
  components: {
    CCorruptedFilesModal,
    CPagination,
    FieldError,
    CFileUploader
  },
  props: formFieldProps,
  setup(props) {
    const { name, specs } = toRefs(props)
    const { state, getters } = useNamespacedStore<FormState>(CLASSIFIED_FORM_NS)
    const { t, locale } = useI18n()
    const logger = useLogger()

    const isVehiclesXymaOrPartsForm = computed(() =>
      getters('isVehiclesXymaOrPartsForm')
    )

    const classifiedId = computed(() => state.classifiedId)
    const editMode = computed(() => state.editMode)
    const vehicleModelImage = computed(() => getters('vehicleModelImage'))
    const isRentalsForm = computed(() => getters('isRentalsForm'))

    const nativePhotos = ref<UploaderFile[]>([])
    const panoramicPhotos = ref<UploaderFile[]>([])

    const loading = ref<boolean>(true)
    const beforeAddIndex = ref<number>(0)
    const maxNativeImages = ref(DEFAULT_MAX_NATIVE_CLASSIFIED_PHOTOS)
    const maxPanoramicImages = ref(DEFAULT_MAX_PANORAMIC_CLASSIFIED_PHOTOS)

    const corruptedFiles = ref<File[]>([])
    const showCorruptedFilesModal = ref(false)

    const { error, valueChanged } = useClassifiedForm(props)
    const { resizeAndReadImage, fileToBase64, loadHeic2Any } = useFile()

    const [userAgentService, photosService] = useDeps(
      UserAgentService,
      PhotosService
    )
    const possibleLockdownMode = ref(false)
    const fileUploaderRef = ref(null)

    const isIos = computed(() => userAgentService.isIos())

    photosService.setUrl(
      editMode.value
        ? `/api/classifieds/${classifiedId.value}/photos/`
        : '/api/classifieds/photos/'
    )

    const { uuid } = useId()
    const { createQueue } = useConcurrentQueue()
    const nativeImagesQueue = createQueue({
      concurrency: editMode.value ? 1 : 7
    })
    const panoramicImagesQueue = createQueue({
      concurrency: editMode.value ? 1 : 7
    })

    async function loadPhotos() {
      const { limits, photos } = await photosService.getPhotosInfo()
      maxNativeImages.value =
        limits?.native ||
        specs.value?.max_photos ||
        DEFAULT_MAX_NATIVE_CLASSIFIED_PHOTOS
      maxPanoramicImages.value =
        limits?.panoramic || DEFAULT_MAX_PANORAMIC_CLASSIFIED_PHOTOS

      nativePhotos.value = (photos?.native || []).map(mapPhotoToUploaderFile)
      panoramicPhotos.value = (photos?.panoramic || []).map(
        mapPhotoToUploaderFile
      )

      loading.value = false
    }

    function mapPhotoToUploaderFile(photo: Photo): UploaderFile {
      return {
        name: photo.name,
        uploading: false,
        thumb: photo.sizes.p,
        attached: true,
        id: uuid(),
        title: photo.title,
        error: ''
      }
    }

    async function handleNativeImagesAdd(files: File[]) {
      beforeAddIndex.value =
        nativePhotos.value.length > 0 ? nativePhotos.value.length - 1 : 0

      for (const file of files) {
        let thumb, photo, currentFileIndex
        try {
          const {
            thumb: resizedThumb,
            photo: resizedPhoto
          } = await resizeAndReadImage({
            file,
            loading
          })

          thumb = resizedThumb
          photo = resizedPhoto

          const id = uuid()
          nativePhotos.value.push({
            thumb,
            name: '',
            uploading: true,
            attached: false,
            id,
            progress: 0,
            error: ''
          })
          currentFileIndex = nativePhotos.value.findIndex(
            (f: UploaderFile) => f.id === id
          )
        } catch (e) {
          if (e.message === 'file_corrupted') {
            corruptedFiles.value.push(file)
            continue
          } else {
            logger.captureError(e)
            vue3Set(
              nativePhotos.value[currentFileIndex],
              'error',
              t('something went wrong')
            )

            if (possibleLockdownMode.value) {
              alert(
                `${t('lockdown_description', {
                  lockdown: t('lockdown_title')
                })} ${t('lockdown_description_2')} `
              )
              break
            }
          }
        }

        nativeImagesQueue.push(async () => {
          try {
            const {
              file: uploadedFile
            } = await photosService.uploadPhoto(
              photo,
              isVehiclesXymaOrPartsForm.value,
              progressEvt =>
                vue3Set(
                  nativePhotos.value[currentFileIndex],
                  'progress',
                  Math.round((progressEvt.loaded * 100) / progressEvt.total!)
                )
            )
            vue3Set(
              nativePhotos.value[currentFileIndex],
              'name',
              uploadedFile.name
            )
            vue3Set(nativePhotos.value[currentFileIndex], 'attached', true)
            vue3Set(nativePhotos.value[currentFileIndex], 'uploading', false)
            updateValue()
          } catch (error) {
            vue3Set(
              nativePhotos.value[currentFileIndex],
              'error',
              (error as any)?.response?.data?.message ||
                t('something went wrong')
            )
          }
        })
      }
      if (fileUploaderRef.value) {
        fileUploaderRef.value.scrollNotVisibleFileAtIndex(beforeAddIndex.value)
      }

      if (corruptedFiles.value.length) {
        showCorruptedFilesModal.value = true
      }
    }

    const onCorruptedOk = () => {
      showCorruptedFilesModal.value = false
      corruptedFiles.value = []
    }

    function handleNativeImagesDelete({
      name: imageName,
      id: imageId
    }: {
      name: string
      id: string
    }) {
      if (editMode.value) {
        photosService.deletePhoto(imageName)
      }
      nativePhotos.value = nativePhotos.value.filter(
        (p: UploaderFile) => p.id !== imageId
      )
      updateValue()
    }

    function handleNativeImagesChange(photos: UploaderFile[]) {
      if (editMode.value) {
        photosService.updatePhotos(photos.map(f => f.name))
      }
      nativePhotos.value = photos
      updateValue()
    }

    async function handlePanoramicImagesAdd(files: File[]) {
      for (const file of files) {
        const id = uuid()
        panoramicPhotos.value.push({
          thumb: await fileToBase64(file),
          name: '',
          uploading: true,
          attached: false,
          id,
          progress: 0,
          error: undefined
        })
        const currentFileIndex = panoramicPhotos.value.findIndex(
          (f: UploaderFile) => f.id === id
        )!

        panoramicImagesQueue.push(async () => {
          try {
            const { file: uploadedFile } = await photosService.uploadPhoto(
              file,
              isVehiclesXymaOrPartsForm.value,
              progressEvt =>
                vue3Set(
                  panoramicPhotos.value[currentFileIndex],
                  'progress',
                  Math.round((progressEvt.loaded * 100) / progressEvt.total!)
                ),
              true
            )
            vue3Set(
              panoramicPhotos.value[currentFileIndex],
              'name',
              uploadedFile.name
            )
            vue3Set(panoramicPhotos.value[currentFileIndex], 'attached', true)
            vue3Set(panoramicPhotos.value[currentFileIndex], 'uploading', false)
            // if the photo is determined as non-panoramic from the backend, move it to the native photos
            if (!uploadedFile.isPanoramic) {
              // using filter instead of pop here in case the photos were moved while uploading
              const fileToRemove = panoramicPhotos.value[currentFileIndex]
              panoramicPhotos.value = panoramicPhotos.value.filter(
                p => p.id !== fileToRemove.id
              )
              nativePhotos.value.push(fileToRemove)
            }
            updateValue()
          } catch (error) {
            if (panoramicPhotos.value[currentFileIndex]) {
              vue3Set(
                panoramicPhotos.value[currentFileIndex],
                'error',
                (error as any)?.response?.data?.message ||
                  t('something went wrong')
              )
            }
          }
        })
      }
    }

    function handlePanoramicImagesDelete({
      name,
      id
    }: {
      name: string
      id: string
    }) {
      if (editMode.value) {
        photosService.deletePhoto(name, true)
      }
      panoramicPhotos.value = panoramicPhotos.value.filter(
        (p: UploaderFile) => p.id !== id
      )
      updateValue()
    }
    function handlePanoramicImagesChange(photos: UploaderFile[]) {
      if (editMode.value) {
        photosService.updatePhotos(
          photos.map(f => f.name),
          true
        )
      }
      panoramicPhotos.value = photos
      updateValue()
    }

    function handlePanoramicImagesTitleChange() {
      updateValue()
    }

    onMounted(async () => {
      if (isIos.value) {
        possibleLockdownMode.value = userAgentService.isLockdownMode()
      }
      await loadPhotos()
    })

    const isPlotForm = computed(() => getters('isPlotForm'))

    const icons = {
      info: faInfo,
      logo360: ci360Logo
    }

    function updateValue() {
      valueChanged(name.value, {
        native: {
          values: nativePhotos.value.map(p => ({
            name: p.name
          }))
        },
        panoramic: {
          values: panoramicPhotos.value.map(p => ({
            name: p.name,
            title: p.title
          }))
        }
      })
    }

    async function visibilityChanged(isVisible: boolean) {
      if (isIos.value && isVisible) {
        try {
          await loadHeic2Any()
        } catch (e) {
          logger.captureError(e)
        }
      }
    }

    return {
      classifiedId,
      editMode,
      error,
      icons,
      vehicleModelImage,
      maxNativeImages,
      maxPanoramicImages,
      loading,
      nativePhotos,
      panoramicPhotos,
      isPlotForm,
      isRentalsForm,
      handleNativeImagesDelete,
      handleNativeImagesChange,
      handleNativeImagesAdd,
      handlePanoramicImagesAdd,
      handlePanoramicImagesDelete,
      handlePanoramicImagesChange,
      handlePanoramicImagesTitleChange,
      isIos,
      visibilityChanged,
      possibleLockdownMode,
      t,
      locale,
      showCorruptedFilesModal,
      corruptedFiles,
      onCorruptedOk,
      fileUploaderRef
    }
  },
  i18n: defineComponentTranslations({
    lockdown_title: {
      en: 'Lockdown mode',
      el: 'Λειτουργία Απομόνωσης'
    },
    lockdown_description: {
      en:
        'We have detected that you have enabled {lockdown} for your iOS device.',
      el:
        'Εντοπίσαμε πως έχεις ενεργοποιήσει την {lockdown} στην iOS συσκευή σου.'
    },
    lockdown_description_2: {
      en:
        "If you wish to upload files to car.gr, you must disable this feature in your device's security settings, as it prohibits access to the photos or videos you want to upload.",
      el:
        'Εάν επιθυμείς να ανεβάσεις αρχεία στον ιστότοπο του car.gr πρέπει να απενεργοποιήσεις αυτή την λειτουργία στις ρυθμίσεις ασφαλείας της συσκευής σου, καθώς απαγορεύει την πρόσβαση στις φωτογραφίες ή τα βίντεο που θέλεις να ανεβάσεις.'
    },
    lockdown_cta: {
      en: 'Open settings',
      el: 'Άνοιγμα ρυθμίσεων'
    },
    first_image_primary: {
      en: 'The first photo is also the one that appears {bold}',
      el: 'H πρώτη φωτογραφία είναι και αυτή που εμφανίζεται στην {bold}'
    },
    first_image_primary_bold: {
      en: 'in results',
      el: 'λίστα αποτελεσμάτων'
    },
    phone_orientation_text: {
      en: 'Take your photos holding your phone in a {bold}',
      el: 'Βγάλε τις φωτογραφίες σου κρατώντας το κινητό σε {bold}'
    },
    phone_orientation_text_bold: {
      en: 'horizontal position',
      el: 'οριζόντια θέση'
    }
  })
})
